import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2 } from "lucide-react";

const toLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const toIso = (local) => (local ? new Date(local).toISOString() : "");

const EMPTY = { title: "", description: "", type: "vip_member", start_at: "", end_at: "", threshold: 0, top_n: 20, min_account_age_days: 0, prizes: [] };

export const TYPE_LABEL = { vip_member: "VIP Member — Panda Coins spent", live_creator: "Live Creator — Panda Bamboo earned" };
export const METRIC_UNIT = { vip_member: "coins", live_creator: "🎋" };

export default function ChallengeForm({ open, onOpenChange, initial, onSubmit, busy }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => {
    if (!open) return;
    setF(initial ? { ...EMPTY, ...initial, start_at: toLocal(initial.start_at), end_at: toLocal(initial.end_at), prizes: initial.prizes || [] } : EMPTY);
  }, [open, initial]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setPrize = (i, k, v) => setF((s) => ({ ...s, prizes: s.prizes.map((p, j) => (j === i ? { ...p, [k]: v } : p)) }));
  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      title: f.title.trim(), description: f.description, type: f.type,
      start_at: toIso(f.start_at), end_at: toIso(f.end_at),
      threshold: Number(f.threshold) || 0, top_n: Number(f.top_n) || 20,
      min_account_age_days: Number(f.min_account_age_days) || 0,
      prizes: f.prizes.filter((p) => p.label?.trim()).map((p) => ({ rank_from: Number(p.rank_from) || 1, rank_to: Number(p.rank_to) || Number(p.rank_from) || 1, label: p.label.trim() })),
    });
  };
  const field = "bg-zinc-950 border-white/10 rounded-none";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="challenge-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{initial?.id ? "Edit challenge" : "New challenge"}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">Rankings come from the existing Coin / Bamboo ledgers. No new accounting is created.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-xs text-zinc-400 space-y-1 sm:col-span-2">Title
              <Input data-testid="challenge-title" required minLength={2} maxLength={80} value={f.title} onChange={set("title")} className={field} />
            </label>
            <label className="text-xs text-zinc-400 space-y-1 sm:col-span-2">Description
              <Textarea data-testid="challenge-description" maxLength={1000} value={f.description} onChange={set("description")} className={field} rows={3} />
            </label>
            <label className="text-xs text-zinc-400 space-y-1 sm:col-span-2">Challenge type
              <select data-testid="challenge-type" value={f.type} onChange={set("type")} className={`w-full ${field} border text-sm px-3 py-2 text-zinc-200`}>
                {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="text-xs text-zinc-400 space-y-1">Start
              <Input data-testid="challenge-start" type="datetime-local" required value={f.start_at} onChange={set("start_at")} className={field} />
            </label>
            <label className="text-xs text-zinc-400 space-y-1">End
              <Input data-testid="challenge-end" type="datetime-local" required value={f.end_at} onChange={set("end_at")} className={field} />
            </label>
            <label className="text-xs text-zinc-400 space-y-1">Qualification threshold ({METRIC_UNIT[f.type]})
              <Input data-testid="challenge-threshold" type="number" min={0} value={f.threshold} onChange={set("threshold")} className={field} />
            </label>
            <label className="text-xs text-zinc-400 space-y-1">Leaderboard size (Top N)
              <Input data-testid="challenge-topn" type="number" min={1} max={100} value={f.top_n} onChange={set("top_n")} className={field} />
            </label>
            <label className="text-xs text-zinc-400 space-y-1">Min account age (days)
              <Input data-testid="challenge-min-age" type="number" min={0} max={3650} value={f.min_account_age_days} onChange={set("min_account_age_days")} className={field} />
            </label>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest text-zinc-500">Prizes / rewards</span>
              <Button type="button" data-testid="challenge-add-prize" size="sm" variant="ghost" className="h-7 rounded-none border border-white/15 text-xs"
                onClick={() => setF((s) => ({ ...s, prizes: [...s.prizes, { rank_from: s.prizes.length + 1, rank_to: s.prizes.length + 1, label: "" }] }))}>
                <Plus size={12} className="mr-1" /> Add prize
              </Button>
            </div>
            {f.prizes.map((p, i) => (
              <div key={i} className="grid grid-cols-[70px_70px_1fr_32px] gap-2 items-center" data-testid={`challenge-prize-row-${i}`}>
                <Input type="number" min={1} value={p.rank_from} onChange={(e) => setPrize(i, "rank_from", e.target.value)} className={field} placeholder="From" />
                <Input type="number" min={1} value={p.rank_to} onChange={(e) => setPrize(i, "rank_to", e.target.value)} className={field} placeholder="To" />
                <Input value={p.label} onChange={(e) => setPrize(i, "label", e.target.value)} className={field} placeholder="e.g. Top Badge + 5,000 coins" maxLength={120} />
                <button type="button" onClick={() => setF((s) => ({ ...s, prizes: s.prizes.filter((_, j) => j !== i) }))} className="text-zinc-500 hover:text-rose-400"><Trash2 size={14} /></button>
              </div>
            ))}
            {f.prizes.length === 0 && <div className="text-xs text-zinc-600">No prizes yet.</div>}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" onClick={() => onOpenChange(false)} className="rounded-none bg-white/10 hover:bg-white/15 border border-white/15 text-white">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="challenge-form-submit" className="btn-crimson rounded-none">{initial?.id ? "Save changes" : "Create challenge"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
